import React from 'react';
import {
  ROLE_AUDITIVE_LEVEL_2,
  ROLE_REGIONAL_SUPERVISOR,
  ROLE_AUDITIVE_SUPERVISOR,
  SPOKE_ROLES,
  AUDITIVE_ROLES,
  ROLE_GYNECOLOGIST,
  ROLE_CEDAP_MANAGER,
  ROLE_OBSTETRICIAN,
  ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
  ROLE_ADMINISTRATOR,
  ROLE_LHC_MANAGER,
  ROLE_SUPPORT,
  ROLE_FACILITY_MANAGER,
  ROLE_AUDITIVE_LEVEL_3,
  OPHTHALMOLOGY_ROLES,
  ROLE_OPHTHALMOLOGY_LEVEL_2,
  ROLE_OPHTHALMOLOGY_LEVEL_3,
  ROLE_OPHTHALMOLOGY_SUPERVISOR,
  ROLE_CEDAP_MANAGER_READ_ONLY,
  // NEONATOLOGIST_ROLES,
} from '../constants/roles';
import RouteDefinition from '../helpers/Route/RouteDefinition';
import RoutesWrapper from '../helpers/Route/RoutesWrapper';
import { addNewbornRoute, searchNewbornRoute } from '../pages/Newborn/routes';
import { stringToBool } from '../helpers/Object';

/**
 * Export all routes that will be registered through the app.
 */
export const indexRoute = new RouteDefinition({
  path: '/',
  component: React.lazy(() => import('../pages/Index')),
  isPrivate: false,
  register: false,
});

/* USER */ export const loginRoute = new RouteDefinition({
  path: '/user/login',
  isPrivate: false,
  component: React.lazy(() => import('../pages/User/Login')),
  layout: null, // Do not wrap the login inside a page
});

export const logoutRoute = new RouteDefinition({
  path: '/user/logout',
  component: React.lazy(() => import('../pages/User/Logout')),
});

export const forgotPasswordRoute = new RouteDefinition({
  path: '/user/forgot-password',
  isPrivate: false,
  component: React.lazy(() => import('../pages/User/ForgotPassword')),
  layout: null, // Do not wrap the login inside a page
});

export const userProfileRoute = new RouteDefinition({
  path: '/user/profile',
  component: React.lazy(() => import('../pages/User/Profile/Manage')),
  exact: false,
});

export const changeAccountRoute = new RouteDefinition({
  path: '/change-account',
  component: React.lazy(() => import('../pages/User/ChangeAccount')),
  access: (user) => (user.availableAccounts || []).length > 1,
  layout: null,
});

export const resetPasswordRoute = new RouteDefinition({
  path: '/user/reset-password',
  isPrivate: false,
  component: React.lazy(() => import('../pages/User/ResetPassword')),
});

export const editHcpRoute = new RouteDefinition({
  path: '/hcp/:id/edit',
  component: React.lazy(() => import('../pages/User/Profile/Edit/PersonalDataSupport')),
  roles: ROLE_SUPPORT,
});

/* METABOLIC */
export const metabolicDashboardRoute = new RouteDefinition({
  path: '/metabolic/dashboard',
  component: React.lazy(() => import('../pages/Metabolic/Dashboard')),
  roles: SPOKE_ROLES,
});
/* INVITE */
export const acceptInviteRoute = new RouteDefinition({
  path: '/invite/accept/:inviteCode',
  isPrivate: false,
  component: React.lazy(() => import('../pages/Invite/Accept')),
});

export const manageInviteRoute = new RouteDefinition({
  path: '/invite',
  component: React.lazy(() => import('../pages/Invite/Manage')),
  access: (user) => user.hasRole(ROLE_REGIONAL_SUPERVISOR) || user.isLead,
  exact: false,
});

/* ADMINISTRATOR */
export const createSupervisorUserRoute = new RouteDefinition({
  path: '/supervisor/create',
  component: React.lazy(() => import('../pages/User/CreateSupervisor')),
  roles: [ROLE_ADMINISTRATOR, ROLE_SUPPORT],
});

export const manageLhcRoute = new RouteDefinition({
  path: '/lhc',
  component: React.lazy(() => import('../pages/Lhc')),
  roles: [ROLE_ADMINISTRATOR, ROLE_SUPPORT],
  exact: false,
});

export const addLhcManagerRoute = new RouteDefinition({
  path: '/lhc-manager/add',
  component: React.lazy(() => import('../pages/Lhc/AddLhcManager')),
  roles: [ROLE_ADMINISTRATOR, ROLE_SUPPORT],
});

export const addFacilityManagerRoute = new RouteDefinition({
  path: '/facility-manager/add',
  component: React.lazy(() => import('../pages/Facility/AddFacilityManager')),
  roles: ROLE_SUPPORT,
});

/* PAGES */
export const landingRoute = new RouteDefinition({
  path: '/home',
  component: React.lazy(() => import('../pages/Landing')),
  roles: SPOKE_ROLES,
});

export const statisticsLandingRoute = new RouteDefinition({
  path: '/statistics',
  component: React.lazy(() => import('../pages/StatisticsLanding')),
  access: (user) => user.hasRole([ROLE_LHC_MANAGER, ROLE_FACILITY_MANAGER]),
});

export const dashboardRoute = new RouteDefinition({
  path: '/dashboard',
  component: React.lazy(() => import('../pages/Index')),
});

export const privacyRoute = new RouteDefinition({
  path: '/privacy',
  isPrivate: false,
  component: React.lazy(() => import('../pages/Privacy')),
});

export const supportRoute = new RouteDefinition({
  path: '/support',
  component: React.lazy(() => import('../pages/Support')),
});

export const userGuideRoute = new RouteDefinition({
  path: '/user-guide',
  component: React.lazy(() => import('../pages/Manual')),
  access: (user) =>
    !user.hasRole([
      ROLE_AUDITIVE_SUPERVISOR,
      ROLE_REGIONAL_SUPERVISOR,
      ROLE_LHC_MANAGER,
      ROLE_SUPPORT,
      ROLE_FACILITY_MANAGER,
    ]),
});

/* NEWBORN */

export const mainMenuManageNewbornRoute = new RouteDefinition({
  path: '/manage-newborn',
  component: React.lazy(() => import('../pages/Newborn/ManageNewborn')),
  roles: SPOKE_ROLES,
});

export const manageNewbornRoute = new RouteDefinition({
  path: '/newborn/list',
  component: React.lazy(() => import('../pages/Newborn/List')),
  roles: [...SPOKE_ROLES, ROLE_SUPPORT],
});

export const addNewbornWrapperRoute = new RoutesWrapper({
  wrapper: React.lazy(() =>
    import('../pages/Newborn/context/AddNewbornContext').then((m) => ({
      default: m?.AddNewbornProvider,
    }))
  ),
  routes: [addNewbornRoute, searchNewbornRoute],
});

export const transferNewbornRoute = new RouteDefinition({
  path: '/newborn/transfer',
  component: React.lazy(() => import('../pages/Newborn/Transfer')),
  roles: SPOKE_ROLES,
});

export const editNewbornRoute = new RouteDefinition({
  path: '/newborn/:newbornId/edit',
  component: React.lazy(() => import('../pages/Newborn/Edit')),
  roles: [...SPOKE_ROLES, ROLE_SUPPORT],
});

/* SHIPPING */
export const shippingAddSamplesRoute = new RouteDefinition({
  path: '/shipping/:shippingId/add-samples',
  component: React.lazy(() => import('../pages/Shipping/AddSamples')),
});

export const viewShippingRoute = new RouteDefinition({
  path: '/shipping/:shippingId/view',
  component: React.lazy(() => import('../pages/Shipping/View')),
});

export const manageShippingRoute = new RouteDefinition({
  path: '/shipping',
  component: React.lazy(() => import('../pages/Shipping/Manage')),
  exact: false,
  roles: [...SPOKE_ROLES, ROLE_SUPPORT],
});

/* SAMPLE */
export const editSampleRoute = new RouteDefinition({
  path: '/newborn/:newbornId/sample/:sampleId/edit',
  component: React.lazy(() => import('../pages/Sample/Edit')),
  roles: SPOKE_ROLES,
});

export const addSampleRoute = new RouteDefinition({
  path: '/newborn/:newbornId/sample/add',
  component: React.lazy(() => import('../pages/Sample/Add')),
  roles: SPOKE_ROLES,
});

export const viewSampleRoute = new RouteDefinition({
  path: '/newborn/:newbornId/sample/:sampleId',
  component: React.lazy(() => import('../pages/Sample/View')),
  roles: SPOKE_ROLES,
});

export const manageProtocolRoute = new RouteDefinition({
  path: '/newborn/protocols',
  component: React.lazy(() => import('../pages/Newborn/Protocol')),
  roles: SPOKE_ROLES,
});

export const samplesList = new RouteDefinition({
  path: '/newborn/:newbornId/sample/list',
  component: React.lazy(() => import('../pages/Sample/List')),
});

export const newbornDetailsWrapperRoute = new RouteDefinition({
  path: '/newborn/:newbornId',
  component: React.lazy(() => import('../pages/Newborn/Details')),
  exact: false,
});

export const consentRoute = new RouteDefinition({
  path: '/consent',
  isPrivate: false,
  component: React.lazy(() => import('../pages/Consent')),
});

/* AUDITIVE */
export const auditiveBookingRoute = new RouteDefinition({
  path: '/auditive/booking',
  component: React.lazy(() => import('../pages/Auditive/Booking')),
  exact: false,
  roles: ROLE_AUDITIVE_LEVEL_2,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveManagePatientsRoute = new RouteDefinition({
  path: '/auditive/newborn/list',
  component: React.lazy(() => import('../pages/Auditive/Newborn/ManagePatients')),
  exact: false,
  roles: [ROLE_AUDITIVE_LEVEL_2, ROLE_AUDITIVE_LEVEL_3],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const availabilityRoute = new RouteDefinition({
  path: '/auditive/manage-availability',
  component: React.lazy(() => import('../pages/Auditive/Availability')),
  exact: false,
  roles: ROLE_AUDITIVE_LEVEL_2,
  access: (user) => user?.workgroup?.config?.auditiveCalendar === 'slot',
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveDashboardRoute = new RouteDefinition({
  path: '/auditive/dashboard',
  component: React.lazy(() => import('../pages/Auditive/Dashboard')),
  roles: AUDITIVE_ROLES,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveManageNewbornRoute = new RouteDefinition({
  path: '/auditive/newborn/first-level/list/',
  component: React.lazy(() => import('../pages/Auditive/Newborn/List')),
  roles: SPOKE_ROLES,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveSignalingRoute = new RouteDefinition({
  path: '/auditive/newborn/signaled',
  component: React.lazy(() => import('../pages/Auditive/Newborn/Signaled')),
  roles: AUDITIVE_ROLES,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveVisitListRoute = new RouteDefinition({
  path: '/auditive/newborn/:newbornId/visit/:visitId',
  component: React.lazy(() => import('../pages/Auditive/Visit/SecondLevel')),
  exact: false,
  register: false,
  roles: [ROLE_AUDITIVE_LEVEL_2, ROLE_AUDITIVE_SUPERVISOR],
});

export const auditiveNewbornRoute = new RouteDefinition({
  path: '/auditive/newborn/:newbornId',
  component: React.lazy(() => import('../pages/Auditive/Newborn')),
  exact: false,
  roles: [ROLE_AUDITIVE_LEVEL_2, ROLE_AUDITIVE_LEVEL_3, ROLE_AUDITIVE_SUPERVISOR],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveStatistics = new RouteDefinition({
  path: '/auditive/statistics',
  component: React.lazy(() => import('../pages/Auditive/Statistics')),
  exact: false,
  roles: [ROLE_LHC_MANAGER, ROLE_FACILITY_MANAGER],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveSupportRoute = new RouteDefinition({
  path: '/auditive/support',
  component: React.lazy(() => import('../pages/Auditive/Support')),
  exact: false,
  roles: ROLE_SUPPORT,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

// OPHTHALMOLOGY
export const ophthalmologyRoute = new RouteDefinition({
  path: '/ophthalmology/support',
  component: React.lazy(() => import('../pages/Ophthalmology/Support')),
  exact: false,
  roles: ROLE_SUPPORT,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyBookingRoute = new RouteDefinition({
  path: '/ophthalmology/booking',
  component: React.lazy(() => import('../pages/Ophthalmology/Booking')),
  exact: false,
  roles: ROLE_OPHTHALMOLOGY_LEVEL_2,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyDashboardRoute = new RouteDefinition({
  path: '/ophthalmology/dashboard',
  component: React.lazy(() => import('../pages/Ophthalmology/Dashboard')),
  roles: OPHTHALMOLOGY_ROLES,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyManageNewbornRoute = new RouteDefinition({
  path: '/ophthalmology/newborn/first-level/list/',
  component: React.lazy(() => import('../pages/Ophthalmology/Newborn/List')),
  roles: SPOKE_ROLES,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologySignalingRoute = new RouteDefinition({
  path: '/ophthalmology/newborn/signaled',
  component: React.lazy(() => import('../pages/Ophthalmology/Newborn/Signaled')),
  roles: OPHTHALMOLOGY_ROLES,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const availabilityRouteOft = new RouteDefinition({
  path: '/ophthalmology/manage-availability',
  component: React.lazy(() => import('../pages/Ophthalmology/Availability')),
  exact: false,
  roles: ROLE_OPHTHALMOLOGY_LEVEL_2,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyManagePatientsRoute = new RouteDefinition({
  path: '/ophthalmology/newborn/list',
  component: React.lazy(() => import('../pages/Ophthalmology/Newborn/ManagePatients')),
  exact: false,
  roles: [ROLE_OPHTHALMOLOGY_LEVEL_2, ROLE_OPHTHALMOLOGY_LEVEL_3],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyNewbornRoute = new RouteDefinition({
  path: '/ophthalmology/newborn/:newbornId',
  component: React.lazy(() => import('../pages/Ophthalmology/Newborn')),
  exact: false,
  roles: [ROLE_OPHTHALMOLOGY_LEVEL_2, ROLE_OPHTHALMOLOGY_LEVEL_3, ROLE_OPHTHALMOLOGY_SUPERVISOR],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_OPHTHALMOLOGIC),
});

export const ophthalmologyVisitListRoute = new RouteDefinition({
  path: '/ophthalmology/newborn/:newbornId/visit/:visitId',
  component: React.lazy(() => import('../pages/Ophthalmology/Visit/SecondLevel')),
  exact: false,
  register: false,
  roles: [ROLE_OPHTHALMOLOGY_LEVEL_2, ROLE_OPHTHALMOLOGY_SUPERVISOR],
});

/* NOTIFICATION */
export const manageNotificationRoute = new RouteDefinition({
  path: '/notification',
  component: React.lazy(() => import('../pages/Notification')),
  exact: false,
  access: (user) =>
    !user.hasRole([
      ROLE_AUDITIVE_LEVEL_2,
      ROLE_AUDITIVE_LEVEL_3,
      ROLE_REGIONAL_SUPERVISOR,
      ROLE_AUDITIVE_SUPERVISOR,
      ROLE_REFERENT_CONGENITAL_MALFORMATIONS,
      ROLE_GYNECOLOGIST,
      ROLE_OBSTETRICIAN,
      ROLE_CEDAP_MANAGER,
      ROLE_CEDAP_MANAGER_READ_ONLY,
      ROLE_LHC_MANAGER,
      ROLE_SUPPORT,
      ROLE_FACILITY_MANAGER,
    ]),
});

export const viewNotificationRoute = new RouteDefinition({
  path: '/notify/:notificationId',
  component: React.lazy(() => import('../pages/Notification/View')),
});

export const auditiveFirstLevelStatistics = new RouteDefinition({
  path: '/auditive/first-level-statistics',
  component: React.lazy(() => import('../pages/Auditive/FirstLevelStatistics')),
  roles: ROLE_AUDITIVE_SUPERVISOR,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveSecondLevelStatistics = new RouteDefinition({
  path: '/auditive/second-level-statistics',
  component: React.lazy(() => import('../pages/Auditive/SecondLevelStatistics')),
  roles: ROLE_AUDITIVE_SUPERVISOR,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

export const auditiveHsList = new RouteDefinition({
  path: '/auditive/hs-list',
  component: React.lazy(() => import('../pages/Auditive/HS/List')),
  roles: ROLE_AUDITIVE_SUPERVISOR,
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_AUDITIVE),
});

/* REGIONAL SUPERVISOR */
export const manageFacilitiesRoute = new RouteDefinition({
  path: '/facility/list',
  component: React.lazy(() => import('../pages/Facility')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
  exact: false,
});

export const addFacilityRoute = new RouteDefinition({
  path: '/facility/add',
  component: React.lazy(() => import('../pages/Facility/Add')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
});

export const editFacilityRoute = new RouteDefinition({
  path: '/facility/:facilityId/edit',
  component: React.lazy(() => import('../pages/Facility/Edit')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
});

export const facilitiesPerformanceStatisticsRoute = new RouteDefinition({
  path: '/facility/performance-statistics',
  component: React.lazy(() => import('../pages/Facility/PerformanceStatistics')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  exact: false,
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const facilitiesReportPerformanceRoute = new RouteDefinition({
  path: '/facility/performance-statistics/report',
  component: React.lazy(() =>
    import('../pages/Facility/PerformanceStatistics/components/ReportPerformance')
  ),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const metabolicCEDAPPerformanceRoute = new RouteDefinition({
  path: '/facility/performance-statistics/metabolic',
  component: React.lazy(() =>
    import('../pages/Facility/PerformanceStatistics/components/MonitoringCEDAPMetabolic')
  ),
  roles: [ROLE_CEDAP_MANAGER, ROLE_CEDAP_MANAGER_READ_ONLY],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_CEDAP),
});

export const facilitiesBirthDataStatistics = new RouteDefinition({
  path: '/facility/birth-data-statistics',
  component: React.lazy(() => import('../pages/Facility/BirthDataStatistics')),
  roles: ROLE_REGIONAL_SUPERVISOR,
});

export const manageWorkgroupsRoute = new RouteDefinition({
  path: '/workgroup/list',
  component: React.lazy(() => import('../pages/Workgroup/List')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
});

export const manageUsersRoute = new RouteDefinition({
  path: '/user/list',
  component: React.lazy(() => import('../pages/User/List')),
  roles: [ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
});

export const manageSupervisorsRoute = new RouteDefinition({
  path: '/supervisor/list',
  component: React.lazy(() => import('../pages/User/SupervisorList')),
  roles: ROLE_SUPPORT,
});

/* REPORT */
export const manageReportRoute = new RouteDefinition({
  path: '/report/archive',
  component: React.lazy(() => import('../pages/Report/Archive')),
  roles: SPOKE_ROLES,
});

export const downloadReportRoute = new RouteDefinition({
  path: '/report/:reportId/download',
  component: React.lazy(() => import('../pages/Report/Download')),
  roles: SPOKE_ROLES,
});

/** HELP DESK */
export const supportDashboardRoute = new RouteDefinition({
  path: '/help-desk/dashboard',
  component: React.lazy(() => import('../pages/HelpDesk/Dashboard')),
  roles: ROLE_SUPPORT,
});

export const accountsMergeRoute = new RouteDefinition({
  path: '/help-desk/merge-accounts',
  component: React.lazy(() => import('../pages/HelpDesk/AccountsMerge')),
  roles: ROLE_SUPPORT,
});

/* Mortality Registry */
export const mortalityRegistry = new RouteDefinition({
  path: '/mortality-registry',
  component: React.lazy(() => import('../pages/MortalityRegistry')),
  roles: [/* ...NEONATOLOGIST_ROLES */ ROLE_REGIONAL_SUPERVISOR, ROLE_SUPPORT],
  enabled: stringToBool(process.env.REACT_APP_ENABLE_MORTALITY_REGISTRY),
});

export { pediatricCentreRoutes } from '../pages/PediatricCentre/routes';

export * from '../cedap/routes';
